import CryptoJS from "crypto-js";

/**
 * Generate a UUID v4 (RFC4122 compliant)
 * Uses native crypto.randomUUID() if available, falls back to Math.random()
 * @returns UUID string in format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
export function generateUUID(): string {
  // Try native crypto.randomUUID() (modern browsers + HTTPS)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers or HTTP environments (RFC4122 compliant)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate SHA-256 hash of a message using crypto-js
 * @param message - The string to hash
 * @returns Lowercase hexadecimal hash string (64 characters)
 */
export function sha256(message: string): string {
  return CryptoJS.SHA256(message).toString(CryptoJS.enc.Hex);
}

/**
 * Normalize email address for consistent hashing
 * - Trims whitespace
 * - Converts to lowercase
 * @param email - Raw email address
 * @returns Normalized email address
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Normalize phone number for consistent hashing
 * - Removes all non-digit characters except '+'
 * - Converts Dutch numbers starting with '0' to international format '+31'
 * @param phone - Raw phone number (e.g., "06 12 34 56 78" or "+31612345678")
 * @returns Normalized phone number (e.g., "+31612345678")
 */
export function normalizePhone(phone: string): string {
  // Remove all characters except digits and '+'
  let cleaned = phone.replace(/[^\d+]/g, "");

  // Convert Dutch mobile numbers starting with 0 to +31
  if (cleaned.startsWith("0")) {
    cleaned = "+31" + cleaned.substring(1);
  }

  return cleaned;
}
